import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { GlobalSearch } from "./GlobalSearch";
import { BottomNav } from "./BottomNav";
import {
  LayoutDashboard, Sparkles, ShoppingBag, Package, ArrowRightLeft, Users,
  FileText, Wallet, BarChart3, UserCog, Settings, LogOut, CreditCard, Menu, X, Shield, PenLine, Bell, HelpCircle, Landmark,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { api } from "../lib/api";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/produkte", label: "Produkte", icon: Package },
  { group: "Ankauf" },
  { to: "/app/ankauf", label: "Ankäufe", icon: ShoppingBag, end: true },
  { to: "/app/ankauf/neu", label: "Neuer Ankauf", icon: ShoppingBag },
  { to: "/app/verkaeufer", label: "Verkäufer", icon: Users },
  { group: "Verkauf" },
  { to: "/app/verkauf", label: "Verkäufe", icon: ArrowRightLeft, end: true },
  { to: "/app/verkauf/neu", label: "Neuer Verkauf", icon: ArrowRightLeft },
  { to: "/app/rechnungen", label: "Rechnungen", icon: FileText },
  { divider: true },
  { to: "/app/bestand", label: "Warenwirtschaft", icon: Package },
  { to: "/app/kategorien", label: "Kategorien", icon: Package },
  { to: "/app/kunden", label: "Kunden", icon: Users },
  { to: "/app/dokumente", label: "Dokumente", icon: FileText },
  { to: "/app/vertraege", label: "Verträge", icon: PenLine },
  { to: "/app/finanzen", label: "Finanzen", icon: Wallet },
  { to: "/app/steuerberater", label: "Steuerberater", icon: Landmark },
  { to: "/app/auswertungen", label: "Auswertungen", icon: BarChart3 },
  { divider: true },
  { to: "/app/ki-import", label: "L19 KI", icon: Sparkles },
  { to: "/app/mitarbeiter", label: "Mitarbeiter", icon: UserCog },
  { to: "/app/einstellungen", label: "Einstellungen", icon: Settings },
  { to: "/app/abrechnung", label: "Abo & Abrechnung", icon: CreditCard },
];

const STATUS_LABELS = {
  trial: "Testphase", aktiv: "Aktiv", zahlung_ausstehend: "Zahlung ausstehend",
  gesperrt: "Gesperrt", gekuendigt: "Gekündigt",
};

export default function AppLayout() {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [insights, setInsights] = useState(null);

  const loadInsights = () => {
    if (insights !== null) return;
    api.get("/stats/insights").then((r) => setInsights(r.data)).catch(() => setInsights([]));
  };
  useEffect(() => { loadInsights(); }, []);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const sidebar = (testid) => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-zinc-100">
        <Link to="/app" className="flex items-center gap-2" data-testid="app-logo">
          <div className="w-8 h-8 bg-zinc-900 rounded-lg flex items-center justify-center">
            <span className="text-white text-[10px] font-bold font-mono">L19</span>
          </div>
          <span className="font-display font-semibold tracking-tight text-zinc-900">L<span className="text-zinc-400">19</span></span>
        </Link>
        <div className="mt-2 text-[11px] font-mono text-zinc-400 truncate">{tenant?.company_name}</div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-3" data-testid={testid}>
        {NAV.map((item, i) => {
          if (item.divider) return <div key={i} className="my-2 border-t border-zinc-100" />;
          if (item.group) return <div key={i} className="px-2 pt-3 pb-1 text-[10px] font-mono uppercase tracking-wider text-zinc-400">{item.group}</div>;
          const Icon = item.icon;
          return (
            <NavLink key={item.to} to={item.to} end={item.end} onClick={() => setMobileOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/[^a-zäöü]/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-[13px] transition-colors ${
                  isActive ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"}`
              }>
              <Icon className="w-4 h-4 shrink-0" /> {item.label}
            </NavLink>
          );
        })}
        {user?.role === "superadmin" && (
          <NavLink to="/admin" data-testid="nav-super-admin"
            className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-[13px] text-zinc-600 hover:bg-zinc-100 mt-2 border border-zinc-200">
            <Shield className="w-4 h-4" /> Super-Admin
          </NavLink>
        )}
      </nav>
      <div className="p-3 border-t border-zinc-100">
        <div className="flex items-center justify-between px-2">
          <div>
            <div className="text-xs font-medium text-zinc-900">{user?.first_name} {user?.last_name}</div>
            <div className="text-[10px] font-mono text-zinc-400">{STATUS_LABELS[tenant?.status] || user?.role}</div>
          </div>
          <button onClick={doLogout} data-testid="logout-button" className="p-2 text-zinc-400 hover:text-zinc-900 transition-colors">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="app-layout">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-60 bg-white border-r border-zinc-200 z-30 print:hidden">{sidebar("app-sidebar-nav")}</aside>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 print:hidden">
          <div className="absolute inset-0 bg-zinc-900/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-white border-r border-zinc-200">{sidebar("app-sidebar-nav-mobile")}</aside>
        </div>
      )}
      <div className="lg:pl-60">
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-zinc-200 px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap print:hidden">
          <button className="lg:hidden p-2 -ml-2 min-h-[44px] min-w-[44px] flex items-center" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-button">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex-1 min-w-0"><GlobalSearch /></div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            {tenant?.status === "trial" && (
              <Link to="/app/abrechnung" data-testid="trial-banner"
                className="text-xs font-mono bg-zinc-900 text-white px-3 py-1.5 rounded-full hover:bg-zinc-800 transition-colors truncate">
                Testphase bis {tenant?.trial_ends} — Tarif aktivieren
              </Link>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-zinc-500 hover:text-zinc-900 relative" data-testid="topbar-notifications">
                  <Bell className="w-4.5 h-4.5 w-[18px] h-[18px]" />
                  {insights && insights.some((i) => i.key !== "ok") && <span className="absolute top-2 right-2 w-2 h-2 bg-zinc-900 rounded-full" />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80" data-testid="notifications-menu">
                <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-400">L19 KI · Hinweise</div>
                {(insights || []).map((i, x) => (
                  <DropdownMenuItem key={x} className="text-sm text-zinc-700 leading-snug whitespace-normal">{i.text}</DropdownMenuItem>
                ))}
                {!insights && <DropdownMenuItem className="text-sm text-zinc-400">Laden…</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
            <a href="/#faq" className="p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-zinc-500 hover:text-zinc-900" data-testid="topbar-help">
              <HelpCircle className="w-[18px] h-[18px]" />
            </a>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-9 h-9 rounded-full bg-zinc-900 text-white text-xs font-bold flex items-center justify-center" data-testid="topbar-profile">
                  {(user?.first_name?.[0] || "") + (user?.last_name?.[0] || "")}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" data-testid="profile-menu">
                <div className="px-3 py-2">
                  <div className="text-sm font-medium">{user?.first_name} {user?.last_name}</div>
                  <div className="text-xs text-zinc-400 font-mono">{user?.email}</div>
                </div>
                <DropdownMenuItem onClick={() => navigate("/app/einstellungen")} data-testid="profile-settings">Einstellungen</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/app/abrechnung")} data-testid="profile-billing">Abo & Abrechnung</DropdownMenuItem>
                <DropdownMenuItem onClick={doLogout} data-testid="profile-logout">Abmelden</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto pb-24 lg:pb-8 print:p-0 print:max-w-none">
          <Outlet />
        </main>
      </div>
      <div className="print:hidden"><BottomNav /></div>
    </div>
  );
}
